
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

interface TypingIndicatorProps {
  topic: 'guide' | 'performance' | 'trading_strategy' | 'advisor';
}

export const TypingIndicator: React.FC<TypingIndicatorProps> = ({ topic }) => {
  const { t } = useTranslation();
  
  const getTopicColor = () => {
    switch (topic) {
      case 'guide':
        return 'botscheme-royal-purple';
      case 'performance':
        return 'botscheme-ocean-blue';
      case 'trading_strategy':
        return 'botscheme-emerald-green';
      case 'advisor':
        return 'botscheme-amber-gold';
      default:
        return 'primary';
    }
  };
  
  const color = getTopicColor();
  
  const particleVariants = useMemo(() => ({
    animate: {
      y: [0, -6, 0],
      opacity: [0.4, 1, 0.4],
      scale: [0.8, 1.2, 0.8],
    }
  }), []);

  const waveVariants = useMemo(() => ({
    animate: {
      scaleX: [1, 1.2, 1],
      opacity: [0.3, 0.7, 0.3],
    }
  }), []);

  const particles = Array.from({ length: 4 }, (_, i) => (
    <motion.div
      key={`particle-${i}`}
      className={cn(
        "absolute w-0.5 h-0.5 rounded-full",
        `bg-${color}/60`
      )}
      style={{
        left: `${15 + i * 12}%`,
        top: `${Math.random() * 30 + 35}%`,
      }}
      variants={particleVariants}
      animate="animate"
      transition={{
        duration: 1.8 + Math.random() * 0.8,
        repeat: Infinity,
        delay: i * 0.25,
        ease: "easeInOut"
      }}
    />
  ));
  
  return (
    <div className="flex flex-col w-full">
      <motion.div 
        className="text-xs text-muted-foreground font-normal leading-none"
        animate={{ opacity: [0.7, 1, 0.7] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        {t('ai_assistant.thinking')}
      </motion.div>
      
      <div className="relative flex items-center h-6 mt-2 overflow-hidden">
        <motion.div
          className={cn(
            "absolute inset-0 rounded-full opacity-20",
            `bg-gradient-to-r from-transparent via-${color}/20 to-transparent`
          )}
          variants={waveVariants}
          animate="animate"
          transition={{
            duration: 2.2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        <div className="flex items-center gap-1.5 relative z-10">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={`main-dot-${i}`}
              className={cn(
                "h-2 w-2 rounded-full shadow-lg",
                `bg-${color}/90`
              )}
              animate={{ 
                opacity: [0.3, 1, 0.3],
                scale: [0.8, 1.2, 0.8],
                y: [0, -3, 0]
              }}
              transition={{
                duration: 1.6,
                repeat: Infinity,
                delay: i * 0.25,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
        
        {particles}
        
        <motion.div
          className={cn(
            "absolute left-0 right-0 h-0.5 rounded-full blur-sm",
            `bg-${color}/30`
          )}
          animate={{
            opacity: [0, 0.6, 0],
            scaleX: [0.5, 1, 0.5]
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>
      
      <motion.div 
        className="mt-1.5 h-0.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
      >
        <motion.div
          className={cn("h-full rounded-full", `bg-${color}/70`)}
          animate={{
            x: ["-100%", "100%"],
            opacity: [0, 1, 0]
          }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: "easeInOut",
            repeatDelay: 0.4
          }}
        />
      </motion.div>
    </div>
  );
};
